import React, { HTMLAttributeAnchorTarget } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { UserApplicationRole } from '../../constants';
import { useApolloClientsContext } from '../../providers/ApolloClientsProvider';
import { ConfigContext } from '../../providers/ConfigContext';
import { Configuration } from '../../providers/ConfigContext/types';
import { useRoleGuardContext } from '../../providers/RoleGuardContext';
import { useAppDrawerContext } from '../AppDrawerContext';
import { Logo } from '../Logo';
import { UserAgreementView } from './components/UserAgreementView';
import {
  Container,
  Header,
  HeaderContent,
  CloseButton,
  CloseIcon,
  Content,
  NavSection,
  UserSection,
  Username,
  LogoutButton,
  NavItem,
  StyledArrowIcon,
} from './styles';

const BASE_URL = `${window.location.protocol}//${window.location.host}`;
const NAV_ITEMS: NavItemTemplate[] = [
  { key: '', label: 'Home', hrefFn: () => `${BASE_URL}/` },
  {
    key: 'community-admin',
    label: 'Community admin',
    hrefFn: () => `${BASE_URL}/community-admin/`,
  },
  {
    key: 'organization-admin',
    label: 'Organization admin',
    hrefFn: () => `${BASE_URL}/organization-admin/`,
  },
  {
    key: 'port-dues',
    label: 'Port dues',
    hrefFn: () => `${BASE_URL}/port-dues/`,
  },
  {
    key: 'visit-notification',
    label: 'Visit notification',
    hrefFn: () => `${BASE_URL}/visit-notification/`,
  },
  {
    key: 'port-call',
    label: 'Port call',
    hrefFn: () => `${BASE_URL}/port-call/`,
  },
  {
    key: 'visit-view',
    label: 'Visit view',
    hrefFn: () => `${BASE_URL}/visit-view/`,
  },
  {
    key: 'hydrometeo',
    label: 'Hydrometeo',
    hrefFn: () => `${BASE_URL}/hydrometeo/`,
  },
  {
    key: 'configuration',
    label: 'Configuration',
    hrefFn: () => `${BASE_URL}/configuration/`,
  },
  { key: 'mocker', label: 'Mocker', hrefFn: () => `${BASE_URL}/mocker/` },
  {
    key: 'signaling',
    label: 'Signaling',
    hrefFn: () => `${BASE_URL}/signaling/`,
  },
  {
    key: 'map',
    label: 'Map',
    target: '_blank',
    hrefFn: (config) => config.portXchange || '',
  },
];

export const NavigationDrawer = () => {
  const { close } = useAppDrawerContext();
  const { logout, user } = useAuth0();
  const config = ConfigContext.useContainer();
  const { roles } = useRoleGuardContext();
  const navItems = NAV_ITEMS.filter(
    (item) => !item.key || roles.includes(item.key),
  );

  const agreementsEnabled = !!useApolloClientsContext().clients.agreements;

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Logo />
        </HeaderContent>
        <CloseButton onClick={close}>
          <CloseIcon />
        </CloseButton>
      </Header>
      <Content>
        <NavSection>
          {navItems.map((item) => {
            const href = item.hrefFn(config);
            return (
              <NavItem
                key={item.key}
                href={href}
                selected={
                  item.key === ''
                    ? window.location.pathname === '/'
                    : window.location.href.startsWith(href)
                }
                target={item.target}
              >
                {item.label}
                <StyledArrowIcon />
              </NavItem>
            );
          })}
        </NavSection>
        <UserSection>
          <Username>{user?.name}</Username>
          <LogoutButton
            onClick={() =>
              logout({ logoutParams: { returnTo: config.auth0.logout_uri } })
            }
            secondary
          >
            Log out
          </LogoutButton>
        </UserSection>
        {agreementsEnabled && <UserAgreementView onToggle={close} />}
      </Content>
    </Container>
  );
};

interface NavItemTemplate {
  key: UserApplicationRole | '';
  label: string;
  target?: HTMLAttributeAnchorTarget;
  hrefFn: (config: Configuration) => string;
}
